/* @flow */

import {
  assignAll,
  filter,
  times,
  find,
  flatMap,
  flow,
  groupBy,
  head,
  map,
  orderBy,
  partition,
  sortBy,
  omit,
  memoize,
  uniqBy,
} from 'lodash/fp';
import { createSelector, defaultMemoize } from 'reselect';
import dayjs from 'dayjs';

import TaskFragment from '../../views/TaskFragment';
import apolloClient from '../../clients/apollo-client';
import l, { timeString } from '../../utils/localization';
import { calendarEventsBetween, calendarEventsRestOfDay } from '../calendar/selectors';
import { deserializeTodoItem } from './transform';


const minDate = new Date(0);

memoize.Cache = WeakMap;

const relativeTimeCalendarMapper = (referenceDay) => (event) => {
  const { endDate, startDate, allDay } = event;

  const end = dayjs(endDate),
        start = dayjs(startDate),
        startSameDay = referenceDay.valueOf() === start.startOf('day').valueOf(),
        endsSameDay = referenceDay.valueOf() === end.startOf('day').valueOf();

  const timeDescription = startSameDay && endsSameDay ? `${start.format(timeString)} - ${end.format(timeString)}`
    : startSameDay ? `${start.format(timeString)} - ${end.format(`dddd ${timeString}`)}`
      : endsSameDay ? `${start.format(`dddd ${timeString}`)} - ${end.format(`${timeString}`)}` : `${start.format(`dddd ${timeString}`)} - ${end.format(`dddd ${timeString}`)}`;

  return {
    ...event, timeDescription: allDay ? l.allDay : timeDescription, startSameDay, endsSameDay,
  };
};

export const numberOfItemsWaitingToSyncSelector = createSelector(
  (state) => state.offline.outbox,
  (outbox) => outbox.length,
);

export const lastFetchTimeSelector = (state: AppState) => state.todo.lastFetchTime;

const getNextItem = (previousItem) => {
  const {
          nextOccurrence: occursAt,
          interval,
          id,
        } = previousItem.recurring,
        occursAtObject = dayjs(occursAt),
        hours = occursAtObject.hour(),
        minutes = occursAtObject.minute(),
        nextOccurrence = occursAtObject.add(1, interval).hour(hours).minute(minutes)
          .toDate(),
        nextItem = {
          ...previousItem,
          id: `${id}_${occursAtObject.toISOString()}`,
          recurring: {
            ...previousItem.recurring,
            lastTimeOfChange: minDate,
            nextOccurrence,
          },
          postponedUntil: occursAtObject.toDate(),
          status: occursAtObject.toDate() > new Date() ? 'postponed' : 'todo',
          isVirtual: true,
        };
  return nextItem;
};

const buildRecurring = (
  previousItem: RecurringTodoItem,
  showUntilDate: Date = new Date(),
) => {
  const nextItem = getNextItem(previousItem);

  return nextItem.postponedUntil > showUntilDate
    ? [nextItem]
    : [nextItem, ...buildRecurring(nextItem, showUntilDate)];
};

const virtualTodoPriority = orderBy('recurring.lastTimeOfChange', 'desc');

export const getVirtualTodos = flow(
  virtualTodoPriority,
  groupBy('recurring.id'),
  flatMap((todos) => {
    const latestRecurringItem = head(todos),
          { recurring: { interval }, title } = latestRecurringItem;

    if (!latestRecurringItem.recurring.isEnabled) {
      return todos;
    }

    const allItems = [
      ...todos.map((t) => ({ ...t, recurring: { ...t.recurring, interval }, title })),
      ...buildRecurring(
        latestRecurringItem,
        dayjs().add(6, 'days').toDate(),
      ),
    ];

    return uniqBy((i) => i.recurring.nextOccurrence.valueOf(), allItems);
  }),
);

export const enhanceTodosWithOutbox = (
  todos: Array<TodoItem>,
  outbox: Array<Object>,
  patchedTodos: Array<Object>,
) => {
  todos = patchedTodos && patchedTodos.length > 0 // eslint-disable-line
    ? uniqBy('id', [...patchedTodos, ...todos])
    : todos;

  if (outbox.length > 0) {
    const newTodos = flow(
      filter((a) => a.type === 'ADD_TODO_REQUEST'),
      map(({ payload: { timeOfChange, changeId, ...payload } }) => ({
        ...payload,
        lastTimeOfChange: timeOfChange,
      })),
    )(outbox);

    const patchIds = outbox
            .filter((a) => a.type === 'PATCH_TODO_REQUEST')
            .map(({ payload }) => ({ id: payload.id, needsCacheItem: true })),
          allTodos = uniqBy('id', [...todos, ...newTodos, ...patchIds]);

    return allTodos.map((t) => {
      const allRelatedChangeSets = flow(
        filter((a) => a.type === 'PATCH_TODO_REQUEST' && a.payload.id === t.id),
        map((a) => ({ ...a.payload, lastTimeOfChange: a.payload.timeOfChange })),
      )(outbox);
      if (allRelatedChangeSets.length === 0) {
        return t;
      }
      t = t.needsCacheItem ? apolloClient.readFragment({ //eslint-disable-line
        id: `TodoType:${t.id}`,
        fragment: TaskFragment,
      }) : t;
      return omit(['timeOfChange', 'changeId'], assignAll([t, ...allRelatedChangeSets]));
    });
  }
  return todos;
};

export const todosWithAppliedChangesFromOutboxSelector = createSelector(
  (state: AppState) => state.todo.items,
  (state: AppState) => state.offline.outbox,
  (todos, outbox) => enhanceTodosWithOutbox(todos, outbox, []),
);

export const allTodosTransformedRaw = createSelector(
  todosWithAppliedChangesFromOutboxSelector,
  (state: AppState) => state.todo.nextStatusChange, // trigger after nextStatusChange
  () => new Date().getTimezoneOffset(), // trigger when timezone is changed
  (allTodos) => map(deserializeTodoItem(new Date()))(allTodos),
);

const getNext = (recurringAndPostponed) => {
  const [next, ...rest] = flow(
    sortBy((item) => item.recurring && item.isRecurringTemplate
      ? item.recurring.nextOccurrence
      : item.postponedUntil),
  )(recurringAndPostponed);

  if (next.isRecurringTemplate) {
    const nextItem = getNextItem(next);

    return { next: omit(['isRecurringTemplate'], nextItem), rest: [...rest, nextItem] };
  }

  return { next, rest };
};

function* getNextGenerator(recurringAndPostponed: Array<TodoItem>, recurringGrouped: Object) {
  let rest = recurringAndPostponed;

  while (rest.length > 0) {
    let result = null,
        foundMatch = false;

    do {
      foundMatch = false;
      result = getNext(rest);
      rest = result.rest; // eslint-disable-line prefer-destructuring

      const { recurring, isVirtual } = result.next;
      if (recurring && isVirtual) {
        const group = recurringGrouped[recurring.id];
        foundMatch = group.find(
          ({
            recurring: { nextOccurrence },
          }) => Math.abs(
            nextOccurrence.valueOf() - recurring.nextOccurrence.valueOf(),
          ) <= 60 * 1000 * 60, // compensate for minor time zone differences
        );
      }
    } while (foundMatch);
    yield result.next;
  }
}

export const futureTasksGenerator = createSelector(
  allTodosTransformedRaw,
  (allTodos) => {
    const recurringAndPostponed = filter((t) => t.recurring || t.status === 'postponed')(allTodos),
          [recurring, postponedNotRecurring] = partition((i) => i.recurring)(recurringAndPostponed);

    const recurringGrouped = flow(
      virtualTodoPriority,
      groupBy((i) => i.recurring.id),
    )(recurring);

    const recurringTemplates = flow(
      map((g) => ({ ...g[0], isRecurringTemplate: true })),
      filter((g) => g.recurring.isEnabled),
    )(recurringGrouped);

    const recurringThatArePostponed = filter((i) => i.status === 'postponed')(recurring);

    return getNextGenerator([
      ...recurringTemplates,
      ...postponedNotRecurring,
      ...recurringThatArePostponed,
    ], recurringGrouped);
  },
);

const getFutureTask = defaultMemoize(
  (generator) => {
    const innerFunction = memoize((index) => generator.next().value); // eslint-disable-line no-unused-vars
    return innerFunction;
  },
);

export const activeVirtualTodosSelector = createSelector(
  futureTasksGenerator,
  (generator) => {
    let done = false;
    const items = [];
    while (!done) {
      const item = getFutureTask(generator)(items.length);
      if (item && item.status === 'todo') {
        items.push(item);
      } else {
        done = true;
      }
    }
    return items;
  },
);

export const futureTodosSelector = createSelector(
  futureTasksGenerator,
  activeVirtualTodosSelector,
  (_, props) => (props && props.itemCount) || 20,
  (generator, activeVirtuals, take) => flow(
    times((index) => getFutureTask(generator)(index + activeVirtuals.length)),
    filter((i) => i),
  )(take),
);

const futureTodosBetweenRawInner = (generator, activeVirtuals, from, until) => {
  const todos = [];
  let done = false,
      index = 0;

  while (!done) {
    const next = getFutureTask(generator)(index + activeVirtuals.length);
    if (!next || until < next.postponedUntil) {
      done = true;
    } else if (next.postponedUntil >= from && next.postponedUntil <= until) {
      if (!todos.find(({ id }) => id === next.id)) {
        todos.push(next);
      }
    }
    index += 1;
  }
  return todos;
};

const futureTodosBetweenRawRange = createSelector(
  futureTasksGenerator,
  activeVirtualTodosSelector,
  (_, { from }) => from,
  (_, { until }) => until,
  futureTodosBetweenRawInner,
);

const futureTodosRestOfDay = createSelector(
  futureTasksGenerator,
  activeVirtualTodosSelector,
  () => dayjs().startOf('day').valueOf(),
  () => dayjs().endOf('day').valueOf(),
  futureTodosBetweenRawInner,
);

const activeTodosSelector = createSelector(
  allTodosTransformedRaw,
  (todos) => todos.filter((i) => i.status === 'todo'),
);

export const fullDailyViewSelector = createSelector(
  activeTodosSelector,
  activeVirtualTodosSelector,
  futureTodosRestOfDay,
  calendarEventsRestOfDay,
  (activeTodos, virtualTodos, futureTodos, events) => {
    const calendarMapper = relativeTimeCalendarMapper(dayjs().startOf('day'));
    const val = flow(
      filter((i) => !i.allDay),
      orderBy([(i) => i.status === 'postponed' || (i.isCalendarEvent && dayjs(i.startDate).valueOf() > new Date().valueOf())
        ? i.postponedUntil.valueOf()
        : -9999999999999, (i) => i.isCalendarEvent ? 9999999999999 : i.manualSortOrder,
      ], ['asc', 'desc']),
    )(
      [...activeTodos, ...virtualTodos, ...futureTodos, ...events.map(calendarMapper)],
    );

    return val;
  },
);

export const nowViewSelector = createSelector(
  fullDailyViewSelector,
  (allForToday) => flow(
    filter((i) => !i.isCalendarEvent
      ? i.status === 'todo'
      : i.startDate < new Date() && i.endDate > new Date() && !i.allDay),
    // orderBy(['isCalendarEvent'], ['asc'])
  )(allForToday),
);


export const futureTodosBetween = createSelector(
  futureTodosBetweenRawRange,
  (futureTodos, allEvents) => flow(
    orderBy(['postponedUntil', 'manualSortOrder'], ['asc', 'desc']),
  )([...futureTodos, ...allEvents]),
);

export const dataPerDaySelector = createSelector(
  futureTodosBetweenRawRange,
  calendarEventsBetween,
  (_, { from }) => from,
  (_, { until }) => until,
  (data, calendarEvents, from, until) => {
    if (from > until) {
      throw new Error('Not ok');
    }
    let day = dayjs(from).startOf('day');
    let tasksLeft = data;
    let result = {};
    while (day.valueOf() <= until.valueOf()) {
      const nextDay = day.add(1, 'day'),
            calendarMapper = relativeTimeCalendarMapper(day.valueOf()),
            calendarEventsToday = flow(
              filter((e) => day.valueOf() < e.endDate.valueOf() && nextDay.valueOf() > e.startDate.valueOf()),
              map(calendarMapper),
            )(calendarEvents),
            [today, rest] = partition((i) => i.postponedUntil.valueOf() >= day.valueOf() && i.postponedUntil.valueOf() < nextDay.valueOf())(tasksLeft);

      tasksLeft = rest;

      const allToday = orderBy(['postponedUntil', 'manualSortOrder'], ['asc', 'desc'])([...today, ...calendarEventsToday]);

      result = { ...result, [day.valueOf()]: allToday };

      day = nextDay;
    }

    return result;
  },
);

export const allTodosTransformed = createSelector(
  allTodosTransformedRaw,
  (allTodos) => {
    const [recurringTodos, singleTodos] = flow(
            partition((i) => i.recurring),
          )(allTodos),
          allRecurring = getVirtualTodos(recurringTodos);

    return [...singleTodos, ...allRecurring];
  },
);

export const nextStatusChangeSelector = createSelector(
  (state) => futureTodosSelector(state, { itemCount: 1 }),
  () => new Date().valueOf(),
  (nextItems) => nextItems && nextItems.length > 0 ? nextItems[0].postponedUntil : null,
);

export const postponedTodosGroupedByTimeSelector = createSelector(
  futureTodosSelector,
  flow(
    filter((i) => i.status === 'postponed'),
    orderBy(['recurring.interval', 'lastTimeOfChange'], ['desc', 'desc']),
    groupBy((i) => i.postponedUntil.valueOf()),
    map.convert({ cap: false })((data, key) => ({
      data,
      key: parseInt(key, 10),
    })),
    orderBy('key', 'asc'),
  ),
);

export const doneTodosSortedByTime = createSelector(
  allTodosTransformedRaw,
  (allTodos) => flow(filter((i) => i.status === 'done'), orderBy('lastTimeOfChange', 'desc'))(
    allTodos,
  ),
);

export const deletedTodosSortedByTime = createSelector(
  allTodosTransformedRaw,
  (allTodos) => flow(filter((i) => i.status === 'deleted'), orderBy('lastTimeOfChange', 'desc'))(
    allTodos,
  ),
);


export const activeTodoCountSelector = createSelector(
  nowViewSelector,
  (nowTodos) => nowTodos.filter((t) => !t.isCalendarEvent).length,
);

export const relatedRecurringItemIdsSelector = (
  state: AppState,
  recurringId: string,
) => recurringId
  ? flow(
    filter(
      (i) => i.recurring && i.recurring.id === recurringId && !i.isVirtual,
    ),
    map((i) => i.id),
  )(allTodosTransformed(state))
  : [];

export const relatedRecurringItemSelector = (
  state: AppState,
  recurringId: string,
) => recurringId
  && flow(
    filter((i) => i.recurring && i.recurring.id === recurringId),
    virtualTodoPriority,
    head,
  )(allTodosTransformed(state));

export const itemByIdSelector = (
  state: AppState,
  id: string,
) => {
  const itemById = find((i) => i.id === id, allTodosTransformed(state));

  if (!itemById) {
    return apolloClient.readFragment({
      id: `TodoType:${id}`,
      fragment: TaskFragment,
    });
  }
  return itemById;
};

export const isFetchingSelector = (state: AppState) => state.todo.isFetching;

export const isFetchRequestedByUserSelector = (
  state: AppState,
) => state.todo.isFetchRequestedByUser;

export const isFetchingRequestedByUserSelector = (
  state: AppState,
) => isFetchingSelector(state) && isFetchRequestedByUserSelector(state);

export const totalItemCountSelector = (state: AppState) => state.todo.items.length;

export const undoTextSelector = createSelector(
  (state) => state.todo.undoText,
  (text) => text,
);

export const undoValidUntilSelector = createSelector(
  (state) => state.todo.undoValidUntil,
  (state) => state.offline.outbox,
  (state) => state.offline.undoOutbox,
  (state) => state.todo.undoItems,
  (
    undoValidUntil, outbox, undoOutbox, undoItems,
  ) => undoOutbox && outbox.length > 0 && undoItems && undoValidUntil
    ? undoValidUntil
    : minDate,
);
